import React, { useState, useEffect } from 'react'
import WaveSurfer from 'wavesurfer.js'
import RegionsPlugin from 'wavesurfer.js/dist/plugin/wavesurfer.regions.min'
import { useSelector, useDispatch } from 'react-redux'
import { useDrop, useDragLayer } from 'react-dnd'
import { addClip, updateClip, removeClip, cutClip, selectChange, sortClip, unselectChange } from './editorSlice'
import { MAX_BARS, NUM_BEATS_PER_BAR, NUM_STEPS_PER_BAR, NUM_STEPS_PER_BEAT, MAJOR_TYPES_PRESET, MAJOR_NUMERALS_PRESET, NUMERAL_TYPES } from './editorConstants'
import { SvgIcon } from '@mui/material'
import {isChangeAvailable, getChangeEnd, getChangeLength, transformStepToMeter, getNumeral, getPitchJSX, getTypeJSX, getNumeralJSX, useKeyPressing, calculateDurationInSeconds} from './editorUtils'
import Confirmation from '../../components/Confirmation'
import StyledTooltip from '../../components/StyledTooltip'
import './LoopMap.css'
import {ReactComponent as GrooveLoopIcon} from './icons/groove-loop.svg'
import {ReactComponent as GrooveOneShotIcon} from './icons/groove-one-shot.svg'
import {ReactComponent as LickLoopIcon} from './icons/lick-loop.svg'
import {ReactComponent as LickOneShotIcon} from './icons/lick-one-shot.svg'
import {ReactComponent as Scale} from './icons/scale.svg'
import {ReactComponent as ScaleViewer} from './icons/scale-viewer.svg'
import {ReactComponent as ScaleViewerMobile} from './icons/scale-viewer-mobile.svg'
import useWindowSize from '../../hooks/window-size';

export default function LoopMap(props){
    const RESIZE_BOARDER_WIDTH = 6;
    const HOVER_BORDER_WIDTH_INCREMENT = 2;
    const { setWavesurfer, offsetX, stepWidth, isViewOnly } = props;
    const dispatch = useDispatch();
    const key = useSelector(state => state.editor.key);
    const mode  = useSelector(state => state.editor.mode);
    const bpm = useSelector(state => state.editor.bpm);
    const bars = useSelector(state => state.editor.bars);
    const duration = useSelector(state => state.editor.duration);
    const clips = useSelector(state => state.editor.clips);
    const config = useSelector(state => state.editor.config);
    const status = useSelector(state => state.editor.status);
    const snap = useSelector(state => state.editor.snap);
    const selectedClipIndex = useSelector(state => state.editor.selectedClipIndex);
    const selectedChangeIndex = useSelector(state => state.editor.selectedChangeIndex);
    const [currentStep, setCurrentStep] = useState(-1);
    const [hoveringClipIndex, setHoveringClipIndex] = useState(-1);
    const [holdingClipIndex, setHoldingClipIndex] = useState(-1);
    const [dragMoveOrResize, setDragMoveOrResize] = useState(true); //true for move, false for resize
    const [resizeBeginOrEnd, setResizeBeginOrEnd] = useState(true); //true for begin, false for end
    const [dragStart, setDragStart] = useState(-1);
    const [dragClip, setDragClip] = useState(null);
    const [dragChanged, setDragChanged] = useState(false);
    const [replaceOpen, setReplaceOpen] = useState(false);
    const [replaceClip, setReplaceClip] = useState(null);
    const [replaceClipIndex, setReplaceClipIndex] = useState(-1);
    const [cursor, setCursor] = useState('default');
    const delKeyPressing = useKeyPressing('Delete', 8);
    const [metaKeyPressing, setMetaKeyPressing] = useState(false);
    const [altKeyPressing, setAltKeyPressing] = useState(false);
    const [ctrlKeyPressing, setCtrlKeyPressing] = useState(false);
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >=0;
    const { width } = useWindowSize();
    let clipCut;

    useEffect(() => {
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent || '') || /iPad|iPhone|iPod/i.test(navigator.userAgent || '');
        const wavesurfer = WaveSurfer.create({
            container: '#editor-waveform',
            backend: isSafari ? 'MediaElement' : 'WebAudio',
            cursorWidth: 0,
            barWidth: 2,
            barMinHeight: 1,
            barGap: 1,
            progressColor: '#FFF',
            waveColor: 'rgba(255, 255, 255, 0.4)',
            height: 62,
            normalize: true,
            hideScrollbar: true,
            plugins: [
                RegionsPlugin.create({dragSelection: false})
            ]
        });
        if (!isSafari)
        {
            const gainNode = wavesurfer.backend.ac.createGain();
            wavesurfer.backend.setFilter(gainNode);
        }
        setWavesurfer(wavesurfer);

    }, [setWavesurfer]);

    useEffect(() => {
        if (delKeyPressing && selectedClipIndex !== -1 && selectedChangeIndex !== -1 && holdingClipIndex === -1)
        {
            dispatch(removeClip(selectedClipIndex));
            setHoveringClipIndex(-1);
        }
    }, [delKeyPressing, setHoveringClipIndex]);

    function getDialogX(e)
    {
        const dialog = document.querySelector("div[aria-labelledby='editor-dialog']");
        return e.clientX - (width - dialog.offsetWidth) / 2 + dialog.scrollLeft;
    }

    function getStep(e) {return Math.floor(((getDialogX(e) < offsetX ? offsetX : getDialogX(e)) - offsetX) / stepWidth);}

    function findClipIndex(step){ return clips.findIndex(clip => clip.loopStart <= step && step < clip.loopEnd);}

    function Change(change, changeIndex, clip, clipIndex){
        const changeEnd = getChangeEnd(clip, changeIndex);
        if (changeEnd <= clip.sampleStart || change.start >= clip.sampleEnd)
            return;
        let borderLeft = 0;
        let className = 'clip-content';
        if (status !== 'playing' && change.start > clip.sampleStart)
            borderLeft = '1px solid #FFDCA3';
        else
            className += ' left';
        if (changeEnd >= clip.sampleEnd)
            className += ' right';
        if (clipIndex === selectedClipIndex && changeIndex === selectedChangeIndex)
            className += ' selected'
        className += ' ' + clip.type;
        const type = clip.type === 'groove' ? (clip.numeral ? NUMERAL_TYPES[change.numeral] : change.type) : null;
        return (
        <div className={className} key={changeIndex} style={{borderLeft: borderLeft, width: getChangeLength(clip, changeIndex) * stepWidth}}
        onClick={() => !dragChanged && dispatch(selectChange({clipIndex: clipIndex, changeIndex: changeIndex}))}>
            {status !== 'playing' && (clip.type === 'lick' ? change.position : 
            <React.Fragment>
                <div className='numeral'>{getNumeralJSX(getNumeral(key, change.root), type)}{change.fill != null && width >= 768 ? ' fill' : ''}</div>
                <div className='chord'>{getPitchJSX(change.root)}{getTypeJSX(type)}</div>
                <div className='position'>{change.position}</div>
            </React.Fragment>)}
        </div>
        );
    }

    function Clip(clip, clipIndex){

        const bounds = {
            left: (clip.loopStart * stepWidth).toString() + "px",
            width: ((clip.loopEnd- clip.loopStart) * stepWidth - 2).toString() + "px"
        };
        const group = !isViewOnly && config.groups.find(group => group.id === clip.id && group.type === clip.type);
        let startTip, lengthTip, startChangeTip, lengthChangeTip;
        if (dragClip !== null)
        {
            startTip = transformStepToMeter(clip.loopStart, 1);
            lengthTip = transformStepToMeter(clip.sampleEnd - clip.sampleStart);
            startChangeTip = dragMoveOrResize || resizeBeginOrEnd ? (clip.loopStart < dragClip.loopStart ? '-' : '+') 
                            + transformStepToMeter(Math.abs(clip.loopStart - dragClip.loopStart)) : '';
            lengthChangeTip = !dragMoveOrResize ? (clip.sampleEnd - clip.sampleStart < dragClip.sampleEnd - dragClip.sampleStart ? '-' : '+') 
                            + transformStepToMeter(Math.abs(clip.sampleEnd - clip.sampleStart - (dragClip.sampleEnd - dragClip.sampleStart))) : '';
        }

        let className = 'clip';
        if (holdingClipIndex !== -1)
        {
            className += ' dragging'
            if (holdingClipIndex === clipIndex)
            {
                className += ' target'
                if (!dragMoveOrResize)
                    className += ' resize'
            }
        }
        else if(((isMac && metaKeyPressing) || (!isMac && ctrlKeyPressing)) && status === 'editing' && currentStep > clip.loopStart && currentStep < clip.loopEnd)
        {
            className += ' cutting'
            clipCut = <div className='clip-cut' style={{left: (currentStep * stepWidth).toString() + "px"}}/>
        }
        if (clip.sampleStart > 0)
            className += ' left-short'
        if (clip.sampleEnd < clip.beats * NUM_STEPS_PER_BEAT || clip.loopEnd > bars * NUM_STEPS_PER_BAR)
            className += ' right-short'
        className += ' ' + clip.type;
        if (status === 'playing')
            className += ' playing'

        return (
            <StyledTooltip open={clipIndex === holdingClipIndex && dragChanged} PopperProps={{modifiers: [{name: 'offset', options: {offset: [0, -7]}}]}} placement='bottom' key={clipIndex} arrow
            title={<React.Fragment>
                    <div className='clip-tooltip-line'>
                        <div style={{minWidth: '40px', textAlign: 'right'}}>Start:</div>
                        <div>{startTip}</div>
                        <div style={{minWidth: '30px', textAlign: 'right', color: '#FFB437'}}>{startChangeTip}</div>
                    </div>
                    <div className='clip-tooltip-line'>
                        <div style={{minWidth: '40px', textAlign: 'right'}}>Length:</div>
                        <div>{lengthTip}</div>
                        <div style={{minWidth: '30px', textAlign: 'right', color: '#FFB437'}}>{lengthChangeTip}</div>
                    </div>
                   </React.Fragment>}>
                <div className={className} key={clipIndex} style={bounds} draggable="false"
                onMouseDown={(e) => handleClipMouseDown(e, clip, clipIndex)}
                onMouseEnter={() => setHoveringClipIndex(clipIndex)}
                onMouseOver={() => setHoveringClipIndex(clipIndex)}
                onMouseLeave={() => setHoveringClipIndex(-1)}>
                    <div className={'clip-header ' + clip.type + (holdingClipIndex === clipIndex && dragChanged ? ' dragging' : '')}>
                        <SvgIcon style={{marginLeft: '8px', marginRight: '5px', marginTop: '5px'}}
                        component={clip.type === 'groove' ? (clip.beats === 0 ? GrooveOneShotIcon : GrooveLoopIcon) : (clip.beats === 0 ? LickOneShotIcon : LickLoopIcon)}/>
                        {isViewOnly ? clip.name : (group.name !== undefined ? group.name : group.id)}
                    </div>
                    <div className='clip-body'>
                        {clip.changes.map((value, index) => Change(value, index, clip, clipIndex))}
                    </div>
                </div>
            </StyledTooltip>
        );
    }

    function ClipRemains()
    {
        let remainLeft, remainRight;
        if (!isViewOnly && ((holdingClipIndex !== -1 && !dragMoveOrResize) || (holdingClipIndex === -1 && hoveringClipIndex !== -1 && clips.length > 0)))
        {
            const clip = clips[holdingClipIndex !== -1 ? holdingClipIndex : hoveringClipIndex];
            if (clip.sampleStart > 0)
            {
                const bounds = {
                    left: ((clip.loopStart - clip.sampleStart) * stepWidth + offsetX).toString() + 'px',
                    width: (clip.sampleStart * stepWidth - HOVER_BORDER_WIDTH_INCREMENT).toString() + 'px'
                }
                remainLeft = (
                <div className='clip-remain left' style={bounds} key='left'>
                    <div className='clip-remain-header' />
                    <div className='clip-remain-body' />
                </div>);
            }
            const clipBeyond = bars * NUM_STEPS_PER_BAR < clip.loopEnd;
            if (clip.sampleEnd < clip.beats * NUM_STEPS_PER_BEAT || clipBeyond)
            {
                const bounds = {
                    left: ((clipBeyond ? bars * NUM_STEPS_PER_BAR :clip.loopEnd) * stepWidth + offsetX + HOVER_BORDER_WIDTH_INCREMENT).toString() + 'px',
                    width: (((clip.beats !== 0 ? clip.beats * NUM_STEPS_PER_BEAT - clip.sampleEnd : 0) 
                        + (clipBeyond ? clip.loopEnd - bars * NUM_STEPS_PER_BAR : 0)) * stepWidth - HOVER_BORDER_WIDTH_INCREMENT).toString() + 'px'
                }
                remainRight = (
                <div className='clip-remain right' style={bounds} key='right'>
                    <div className='clip-remain-header' />
                    <div className='clip-remain-body' />
                </div>);
            }
        }
        return [remainLeft, remainRight];
    }
    
    function ClipPlaceHolder()
    {
        let bounds;
        if (dragClip !== null)
            bounds = {
                left: (dragClip.loopStart * stepWidth).toString() + "px",
                width: ((dragClip.loopEnd- dragClip.loopStart) * stepWidth).toString() + "px"
            };
        return (holdingClipIndex !== -1 && dragMoveOrResize &&
             <div className={'clip placeholder ' + dragClip.type} style={bounds}>
                 <div className={'clip-header ' + dragClip.type}/>
                 <div className={'clip-content placeholder ' + dragClip.type}/>
             </div>
        );
    }

    function DropZone()
    {
        function getDropBounds(item, index)
        {
            // const left = ((item.beats >= 4 || item.beats === 0) && index % 2 === 1 ? index - 1 : index) * NUM_STEPS_PER_BEAT;
            const left = index * NUM_STEPS_PER_BEAT;
            const width = (item.beats === 0 ? 4 : item.beats) * NUM_STEPS_PER_BEAT;
            return {
                left: left,
                width: left + width > bars * NUM_STEPS_PER_BAR ? bars * NUM_STEPS_PER_BAR - left : width
            }
        }
        function handleDropClip(item, index)
        {
            const {left, width} = getDropBounds(item, index);
            const group = config.groups.find(group => group.id === item.id && group.type === item.type);
            const clip = {
                id: item.id,
                name: item.name,
                type: item.type,
                beats: item.beats,
                sampleStart: 0,
                sampleEnd: width,
                loopStart: left,
                loopEnd: left + width,
            };
        
            if (clip.type === 'groove')
            {
                clip.numeral = group.numeral;
                const change_numeral_in_major = mode === 'major' ? 'I' : 'VI';
                clip.changes = group.changes.map(change => ({
                    start: (change.beat - 1) * NUM_STEPS_PER_BEAT + change.subdivision - 1,
                    root: key,
                    position: group.positions[Math.floor((group.positions.length - 1) / 2)],
                    fill: null,
                    gain: 0.0
                }));
                for (const change of clip.changes)
                {
                    if (group.numeral)
                        change.numeral = MAJOR_NUMERALS_PRESET[change_numeral_in_major].find(numeral => group.numerals.includes(numeral));
                    else
                        change.type = group.types !== null ? MAJOR_TYPES_PRESET[change_numeral_in_major].find(type => group.types.includes(type)) : '';
                }
            }
            else if(clip.type === 'lick')
                clip.changes = [{start:0, position: group.positions[0], gain: 0.0}];
            
            let i;
            for (i = 1; i < width; i++)
            {
                const blockClipIndex = findClipIndex(left + i);
                if (blockClipIndex !== -1 && clips[blockClipIndex].loopStart === left + i)
                {
                    clip.sampleEnd = i;
                    clip.loopEnd = clip.loopStart + i;
                    break;
                }
            }

            const blockClipIndex = findClipIndex(left);
            if (blockClipIndex !== -1)
            {
                if (clips[blockClipIndex].loopStart === left)
                {
                    setReplaceOpen(true);
                    setReplaceClip({...clip});
                    setReplaceClipIndex(blockClipIndex);
                    return;
                }
                else if (clips[blockClipIndex].loopEnd > left)
                {
                    const blockClip = {...clips[blockClipIndex]};
                    blockClip.sampleEnd -= blockClip.loopEnd - left;
                    blockClip.loopEnd = left;
                    dispatch(updateClip({clipIndex: blockClipIndex, clip: blockClip}));
                }
            }
            dispatch(addClip(clip));
        }

        function DragSpot(index)
        {
            const [{ isOver, canDrop, item }, drop] = useDrop(() => ({
                accept: 'group',
                drop: (item) => handleDropClip(item, index),
                collect: (monitor) => ({
                    isOver: !!monitor.isOver(),
                    canDrop: !!monitor.canDrop(),
                    item: monitor.getItem()
                }),
            }));
            if (index >= bars * NUM_BEATS_PER_BAR)
                return;
            let dropClip;
            if (isOver)
            {
                const {left, width} = getDropBounds(item, index);
                const clipBounds = {
                    left: (left * stepWidth).toString() + 'px',
                    width: (width * stepWidth - 4).toString() + 'px'
                }
                dropClip = <div className='drop-clip' key={'drop-clip' + index.toString()} style={clipBounds}/>
            }
            return (canDrop && [dropClip, <div className='drop-spot' ref={drop} key={index} style={{width: (stepWidth * NUM_STEPS_PER_BEAT).toString() + 'px'}}/>]);
        }

        const { isDragging} = useDragLayer(monitor => ({
            isDragging: !!monitor.isDragging(),
          }));
        
        return (
            <div className='drop-zone' style={{zIndex: isDragging ? 1 : -1}}>
                {[...Array(MAX_BARS * NUM_BEATS_PER_BAR).keys()].map(i => DragSpot(i))}
            </div>
        )
    }

    function handleClipMouseDown(e, clip, clipIndex)
    {
        e.preventDefault();
        e.stopPropagation();
        if (isViewOnly)
            return;
        if (clipCut)
        {
            const step = getStep(e);
            const newClip = {...clip};
            if (clip.beats !== 0)
                newClip.sampleStart += step - newClip.loopStart;
            else
                newClip.sampleEnd -= step - newClip.loopStart;
            newClip.loopStart = step;
            dispatch(cutClip({clip: newClip, clipIndex: clipIndex}));
        }
        else 
        {
            setDragMoveOrResize(getDialogX(e) >= clip.loopStart * stepWidth + offsetX + RESIZE_BOARDER_WIDTH
            && clip.loopEnd * stepWidth + offsetX - getDialogX(e) > RESIZE_BOARDER_WIDTH + 1);
            setResizeBeginOrEnd(e.nativeEvent.offsetX <= RESIZE_BOARDER_WIDTH)
            setDragStart(getStep(e));
            setDragClip({...clip});
            setHoldingClipIndex(clipIndex);
            setDragChanged(false);
        }
    }

    function handleMouseMove(e)
    {
        e.preventDefault();
        setCursor(hoveringClipIndex !== -1 ? (isViewOnly || (getDialogX(e) >= clips[hoveringClipIndex].loopStart * stepWidth + offsetX + RESIZE_BOARDER_WIDTH
        && clips[hoveringClipIndex].loopEnd * stepWidth + offsetX - getDialogX(e) > RESIZE_BOARDER_WIDTH + 1) ? 'pointer' : 'ew-resize') : 'default');
        if (isViewOnly)
            return;
        setMetaKeyPressing(e.metaKey);
        setAltKeyPressing(e.altKey);
        setCtrlKeyPressing(e.ctrlKey);
        setCurrentStep(getStep(e));
        if (holdingClipIndex !== -1)
        {
            const interval = getStep(e) - dragStart;
            if (interval === 0)
            {
                dispatch(updateClip({clipIndex: holdingClipIndex, clip:dragClip}));
                return;
            }
            setDragChanged(true);
            if (dragMoveOrResize)
            {
                if ((interval > 0 && interval + dragClip.loopEnd <= bars * NUM_STEPS_PER_BAR)
                    || (interval < 0 && interval + dragClip.loopStart >= 0))
                {
                    if (snap && (dragClip.loopStart + interval) % NUM_STEPS_PER_BEAT !== 0)
                       return;
                    let i;
                    for (i = dragClip.loopStart + interval; i < dragClip.loopEnd + interval; i++)
                    {
                        const clipIndex = findClipIndex(i);
                        if (clipIndex !== -1 && clipIndex !== holdingClipIndex)
                            return;
                    }
                    const clip = {...dragClip};
                    clip.loopStart += interval;
                    clip.loopEnd += interval;
                    dispatch(updateClip({clipIndex: holdingClipIndex, clip:clip}));
                }
            }
            else if (resizeBeginOrEnd)
            {
                if (interval + dragClip.sampleStart < dragClip.sampleEnd 
                    && (dragClip.beats === 0 || interval + dragClip.sampleStart >= 0)
                    && interval + dragClip.loopStart >= 0 && interval + dragClip.loopStart < bars * NUM_STEPS_PER_BAR)
                {
                    if (interval < 0)
                    {
                        let i;
                        for (i = -1; i >= interval; i--)
                        {
                            const clipIndex = findClipIndex(dragClip.loopStart + i);
                            if (clipIndex !== -1 && clipIndex !== holdingClipIndex)
                                return;
                        }
                    }
                    if (snap && (interval + dragClip.sampleStart) % NUM_STEPS_PER_BEAT !== 0)
                    {
                    	let matchChange = false;
                        for (const change of dragClip.changes)
                    		if (change.start === interval + dragClip.sampleStart)
                    		{
                    			matchChange = true;
                    			break;
                    		}
                    	if (!matchChange)
                    		return;
                    }
                    const clip = {...dragClip};
                    clip.loopStart += interval;
                    if (clip.beats !== 0)
                        clip.sampleStart += interval;
                    else
                        clip.sampleEnd -= interval;
                    dispatch(updateClip({clipIndex: holdingClipIndex, clip:clip}));
                }
            }
            else if (interval + dragClip.sampleEnd > dragClip.sampleStart
                 && (dragClip.beats === 0 || interval + dragClip.sampleEnd <= dragClip.beats * NUM_STEPS_PER_BEAT)
                && interval + dragClip.loopEnd <= bars * NUM_STEPS_PER_BAR)
            {
                if (interval > 0)
                {
                    let i;
                    for (i = 1; i <= interval; i++)
                    {
                        const clipIndex = findClipIndex(dragClip.loopEnd + i - 1);
                        if (clipIndex !== -1 && clipIndex !== holdingClipIndex)
                            return;
                    }
                }
                if (snap && (interval + dragClip.sampleEnd) % NUM_STEPS_PER_BEAT !== 0)
                {
                	let matchChange = false;
                	for (const change of dragClip.changes)
                		if (change.start === interval + dragClip.sampleEnd)
                		{
                			matchChange = true;
                			break;
                		}
                	if (!matchChange)
                		return;
                }
                const clip = {...dragClip};
                clip.loopEnd += interval;
                clip.sampleEnd += interval;
                dispatch(updateClip({clipIndex: holdingClipIndex, clip:clip}));
            }
            
            if (selectedChangeIndex !== -1 && !isChangeAvailable(clips[selectedClipIndex], clips[selectedClipIndex].changes[selectedChangeIndex], bars))
			    dispatch(unselectChange());
        }
    }

    function handleMouseUp(e)
    {
        if (holdingClipIndex !== -1 && dragMoveOrResize && altKeyPressing && dragClip.loopStart !== clips[holdingClipIndex].loopStart)
        {
            const interval = getStep(e) - dragStart;
            const clip = {...dragClip};
            if (interval > 0 && dragClip.loopEnd > clips[holdingClipIndex].loopStart)
            {
                clip.loopEnd = clips[holdingClipIndex].loopStart;
                clip.sampleEnd = clip.loopEnd - clip.loopStart + clip.sampleStart;
            }
            else if(interval < 0 && dragClip.loopStart < clips[holdingClipIndex].loopEnd)
            {
                clip.loopStart = clips[holdingClipIndex].loopEnd;
                clip.sampleStart = clip.sampleEnd - (clip.loopEnd - clip.loopStart);
            }
            dispatch(addClip(clip));
        }
        setHoldingClipIndex(-1);
        if (dragChanged)
        {
            dispatch(sortClip());
            dispatch(unselectChange());
        }
    }

    let hasClipBeyond = false;
    for (const clip of clips)
        if (clip.loopEnd > bars * NUM_STEPS_PER_BAR)
        {
            hasClipBeyond = true;
            break;
        }
    //calculate difference due to mp3 silences on head and tail
    const idealDuration = calculateDurationInSeconds(bpm, bars);
    const regionWidth = stepWidth * bars * NUM_STEPS_PER_BAR;

    return (
        <div className={isViewOnly ? 'loop-map viewer' : 'loop-map'} style={{cursor: holdingClipIndex === -1 ? cursor : (dragMoveOrResize ? (altKeyPressing ? 'copy' : 'grabbing') : 'ew-resize')}}
        onMouseDown={() => dispatch(unselectChange())}
        onMouseMove={e => handleMouseMove(e)} 
        onMouseUp={e => handleMouseUp(e)} 
        onMouseLeave={() => {setHoldingClipIndex(-1);if(dragChanged){dispatch(sortClip())} }}>
            <div className={isViewOnly ? 'loop-map-scale viewer' : 'loop-map-scale'} style={{marginLeft: offsetX.toString() + 'px'}}>
                {isViewOnly ? (width <= 438 ? <ScaleViewerMobile style={{width: '100%'}}/> : <ScaleViewer style={{width: '100%'}}/>) : <Scale style={{width: '100%'}}/>}
            </div>
            <div className={isViewOnly ? 'loop-map-region viewer' : 'loop-map-region'} style={{overflow: hasClipBeyond || status === 'playing' ? 'hidden' : 'visible', marginLeft: offsetX.toString() + 'px', width: (regionWidth).toString() + 'px'}}>
                {clips.map((value, index) => Clip(value, index))}
                <DropZone />
                <ClipPlaceHolder />
                <div id="editor-waveform" className='editor-waveform' style={{transform: status === 'playing' ? 'translateX(-' + ((duration - idealDuration) / 2 / idealDuration * regionWidth).toString() + 'px) scaleX(' + (duration / idealDuration).toString() + ')' : 'none', zIndex: status === 'playing' ? 2 : -1, visibility: status === 'playing' ? 'visible' : 'hidden'}}/>
                {clipCut}
            </div>
            <ClipRemains />
            <Confirmation open={replaceOpen} setOpen={setReplaceOpen} onSubmit={() => dispatch(updateClip({clipIndex: replaceClipIndex, clip:replaceClip}))}
            content={'Replace the existing clip?'}/>
        </div>
    );
}